/**
 * The linear scanners behind `redactTargetSecrets` (security-slice review SBT-01 / 02 / 03 / 06). Each one walks the
 * text once, with per-position lookups built in one pass, and reports the spans to mask; none uses a backtracking
 * regex over unbounded input. The caller caps the input length (`MAX_REDACTION_INPUT`).
 *
 * They over-mask on purpose where text is ambiguous (Fable review S5): an alias `dev/test@corp` shows `dev/***@corp`,
 * `https://h:8080/p?email=user@corp.com` loses its port and path. That is the safe direction. Do not "fix" such a
 * display by bypassing the safe-display helpers (`safeConnectionDisplay.ts`); narrow a scanner here, with a sentinel
 * test proving no credential shape leaks.
 */
export type Span = [start: number, end: number];

const WS = /\s/u;
export const isSpace = (char: string | undefined): boolean => char !== undefined && WS.test(char);

/** Per-position lookups, each built in one pass. */
export type TextIndex = {
  n: number;
  /** The last `@` at or before i (-1). */
  prevAt: Int32Array;
  /** The last `"` at or before i (-1). */
  prevQuote: Int32Array;
  /** The next `"` at or after i (n). */
  nextQuote: Int32Array;
  /** The next whitespace at or after i (n). */
  nextSpace: Int32Array;
  /** The last whitespace at or before i (-1). */
  prevSpace: Int32Array;
  /** The last `?` at or before i (-1). */
  prevQuery: Int32Array;
  /** The next `/`, `?` or `#` at or after i (n): where a URL's authority ends. Whitespace does not end it (SBT-01). */
  nextAuthorityEnd: Int32Array;
};

export function indexText(text: string): TextIndex {
  const n = text.length;
  const prevAt = new Int32Array(n + 1);
  const prevQuote = new Int32Array(n + 1);
  const prevSpace = new Int32Array(n + 1);
  const prevQuery = new Int32Array(n + 1);
  const nextQuote = new Int32Array(n + 1);
  const nextSpace = new Int32Array(n + 1);
  const nextAuthorityEnd = new Int32Array(n + 1);
  let at = -1; let quote = -1; let space = -1; let query = -1;
  for (let i = 0; i < n; i += 1) {
    const c = text[i];
    if (c === '@') at = i;
    else if (c === '"') quote = i;
    else if (c === '?') query = i;
    else if (isSpace(c)) space = i;
    prevAt[i] = at; prevQuote[i] = quote; prevSpace[i] = space; prevQuery[i] = query;
  }
  let nQuote = n; let nSpace = n; let nEnd = n;
  nextQuote[n] = n; nextSpace[n] = n; nextAuthorityEnd[n] = n;
  for (let i = n - 1; i >= 0; i -= 1) {
    const c = text[i];
    if (c === '"') nQuote = i;
    if (c === '/' || c === '?' || c === '#') nEnd = i;
    if (isSpace(c)) nSpace = i;
    nextQuote[i] = nQuote; nextSpace[i] = nSpace; nextAuthorityEnd[i] = nEnd;
  }
  return { n, prevAt, prevQuote, nextQuote, nextSpace, prevSpace, prevQuery, nextAuthorityEnd };
}

/**
 * `scheme://user:password@host` — the password from the user's `:` to the LAST `@` of the authority, which ends at the
 * first `/`, `?` or `#` (the WHATWG URL parser's rule; pg-connection-string encodes spaces first, so a space inside the
 * password is kept, and so is anything else before that delimiter). An authority with no `@` whose whitespace-free
 * token has one later is read as a password holding a raw `/` (a person typed it; mask more). No `:` in the userinfo →
 * a user name only, kept.
 */
export function urlUserinfoSpans(text: string, index: TextIndex, spans: Span[]): void {
  for (let i = text.indexOf('://'); i >= 0; i = text.indexOf('://', i + 3)) {
    const start = i + 3;
    const end = index.nextAuthorityEnd[start];
    let lastAt = end > start ? index.prevAt[end - 1] : -1;
    const colon = text.indexOf(':', start);
    if (lastAt < start && colon >= 0 && colon < end) {
      const tokenEnd = index.nextSpace[start];
      lastAt = tokenEnd > start ? index.prevAt[tokenEnd - 1] : -1;
    }
    if (lastAt >= start && colon >= 0 && colon < lastAt) spans.push([colon + 1, lastAt]);
  }
}

/** Characters that cannot be part of an unquoted Oracle user name; anything else (any script) can (SBT-02). */
const NOT_USER = '/@:=,;\'"()';
const isUserChar = (c: string | undefined): boolean => c !== undefined && !NOT_USER.includes(c) && !isSpace(c) && c >= ' ';
/** What may stand right before a user name: the text start, whitespace, or one of these (`jdbc:oracle:thin:u/…`). */
const BEFORE_USER = ':=,;\'"(';

/**
 * Oracle JDBC thin / SQL*Plus / EZConnect credentials, `user/password@…` — the user unquoted (any script) or quoted, the
 * password quoted, or up to the last `@` of its whitespace-free token; after `thin:` (JDBC) up to the last `@` of the
 * text, since the driver reads a password with spaces there.
 */
export function slashCredentialSpans(text: string, index: TextIndex, spans: Span[]): void {
  const { n } = index;
  for (let i = text.indexOf('/'); i >= 0; i = text.indexOf('/', i + 1)) {
    const next = text[i + 1];
    if (next === undefined || next === '/' || next === '@') continue;
    let userStart: number;
    if (text[i - 1] === '"') {
      userStart = i >= 2 ? index.prevQuote[i - 2] : -1;
      if (userStart < 0) continue;
    } else {
      let j = i - 1;
      while (j >= 0 && isUserChar(text[j])) j -= 1;
      if (j === i - 1) continue;
      userStart = j + 1;
    }
    const before = text[userStart - 1];
    if (userStart > 0 && !isSpace(before) && !BEFORE_USER.includes(before)) continue;
    let end = -1;
    if (next === '"') {
      const close = index.nextQuote[i + 2];
      if (close < n && text[close + 1] === '@') end = close + 1;
    }
    if (end < 0) {
      const jdbc = text.slice(Math.max(0, userStart - 5), userStart).toLowerCase() === 'thin:';
      const limit = jdbc ? n : index.nextSpace[i];
      const lastAt = limit > 0 ? index.prevAt[limit - 1] : -1;
      if (lastAt > i + 1) end = lastAt;
    }
    if (end > i + 1) spans.push([i + 1, end]);
  }
}

/**
 * The text with `%XX` escapes decoded and `+` read as a space — how pg-connection-string / URLSearchParams and the
 * storage service read a query key (SBT-01) — plus, for every view position, the original position it came from.
 * Key names are matched on the view; values are delimited on the original text.
 */
export function decodedView(text: string): { view: string; origin: Int32Array } {
  const origin = new Int32Array(text.length + 1);
  let view = '';
  let i = 0;
  while (i < text.length) {
    origin[view.length] = i;
    const hex = text[i] === '%' ? text.slice(i + 1, i + 3) : '';
    if (hex.length === 2 && /^[0-9A-Fa-f]{2}$/u.test(hex)) {
      view += String.fromCharCode(parseInt(hex, 16));
      i += 3;
    } else {
      view += text[i] === '+' ? ' ' : text[i];
      i += 1;
    }
  }
  origin[view.length] = text.length;
  return { view, origin };
}

/** The text without TAB / LF / CR, and for every view position the original position it came from. */
export function withoutUrlControls(text: string): { view: string; origin: Int32Array } {
  const origin = new Int32Array(text.length + 1);
  const kept: string[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (c === '\t' || c === '\n' || c === '\r') continue;
    origin[kept.length] = i;
    kept.push(c);
  }
  origin[kept.length] = text.length;
  return { view: kept.join(''), origin };
}

/** The spans, sorted and merged; each merged span is replaced by `***`. */
export function applySpans(text: string, spans: Span[]): string {
  if (spans.length === 0) return text;
  spans.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const parts: string[] = [];
  let from = 0;
  let current: Span | null = null;
  for (const span of spans) {
    if (current && span[0] <= current[1]) { current[1] = Math.max(current[1], span[1]); continue; }
    if (current) { parts.push(text.slice(from, current[0]), '***'); from = current[1]; }
    current = [span[0], span[1]];
  }
  if (current) { parts.push(text.slice(from, current[0]), '***'); from = current[1]; }
  parts.push(text.slice(from));
  return parts.join('');
}
